import { useState, useEffect } from "react";
import axios from "axios";
import { ChefHat, BarChart3, TrendingUp } from "lucide-react";
import { Button } from "../ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODI = [
  { value: "settimana", label: "7 giorni" },
  { value: "mese", label: "30 giorni" },
  { value: "anno", label: "12 mesi" },
];

export default function StatistichePreparazioni({ localeId, getAuthHeaders }) {
  const [periodo, setPeriodo] = useState("settimana");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ ricette: [], totale_lotti: 0 });

  useEffect(() => {
    if (!localeId) return;
    let isCancelled = false;
    setLoading(true);
    axios
      .get(`${API}/preparazioni/statistiche?locale_id=${localeId}&periodo=${periodo}`, {
        headers: getAuthHeaders(),
      })
      .then((res) => { if (!isCancelled) setStats(res.data); })
      .catch(() => { if (!isCancelled) setStats({ ricette: [], totale_lotti: 0 }); })
      .finally(() => { if (!isCancelled) setLoading(false); });
    return () => { isCancelled = true; };
  }, [localeId, periodo, getAuthHeaders]);

  const max = Math.max(...(stats.ricette || []).map((r) => r.n_preparazioni), 1);

  return (
    <div className="space-y-4" data-testid="preparazioni-stats">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ChefHat className="h-5 w-5 text-teal-500" />
          <h3 className="text-foreground font-medium">Statistiche preparazioni</h3>
        </div>
        <div className="flex gap-2">
          {PERIODI.map((p) => (
            <Button
              key={p.value}
              variant={periodo === p.value ? "default" : "outline"}
              size="sm"
              onClick={() => setPeriodo(p.value)}
              data-testid={`periodo-${p.value}`}
              className={periodo === p.value ? "bg-teal-500 hover:bg-teal-400 text-white" : ""}
            >
              {p.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Riepilogo */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="bg-card border border-border rounded-2xl p-4">
          <p className="text-xs text-muted-foreground flex items-center gap-1"><BarChart3 className="h-3 w-3" /> Preparazioni totali</p>
          <p className="text-2xl font-bold text-foreground mt-1">{stats.totale_lotti || 0}</p>
        </div>
        <div className="bg-card border border-border rounded-2xl p-4">
          <p className="text-xs text-muted-foreground flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Ricette diverse</p>
          <p className="text-2xl font-bold text-foreground mt-1">{(stats.ricette || []).length}</p>
        </div>
        <div className="bg-card border border-border rounded-2xl p-4 col-span-2 sm:col-span-1">
          <p className="text-xs text-muted-foreground">Top ricetta</p>
          <p className="text-base font-bold text-foreground mt-1 truncate">
            {(stats.ricette || [])[0]?.ricetta_nome || "—"}
          </p>
        </div>
      </div>

      {/* Tabella */}
      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Caricamento...</div>
      ) : (stats.ricette || []).length === 0 ? (
        <div className="text-center py-8 text-muted-foreground bg-card rounded-2xl border border-border">
          <ChefHat className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>Nessuna preparazione registrata in questo periodo</p>
        </div>
      ) : (
        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          <div className="hidden sm:grid sm:grid-cols-12 px-4 py-2 text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
            <div className="sm:col-span-5">Ricetta</div>
            <div className="sm:col-span-2 text-right">N° lotti</div>
            <div className="sm:col-span-3 text-right">Quantità totale</div>
            <div className="sm:col-span-2 text-right">Ultimo</div>
          </div>
          {(stats.ricette || []).map((r, idx) => (
            <div
              key={`${r.ricetta_id}-${r.unita_misura}`}
              className="px-4 py-3 border-b border-border last:border-0 sm:grid sm:grid-cols-12 sm:items-center"
              data-testid={`stat-row-${idx}`}
            >
              <div className="sm:col-span-5">
                <div className="font-medium text-foreground truncate">{r.ricetta_nome}</div>
                <div className="mt-1 h-1.5 bg-muted rounded overflow-hidden">
                  <div
                    className="h-full bg-teal-500"
                    style={{ width: `${(r.n_preparazioni / max) * 100}%` }}
                  />
                </div>
              </div>
              <div className="sm:col-span-2 sm:text-right mt-2 sm:mt-0 text-foreground">
                <span className="text-sm font-semibold">{r.n_preparazioni}</span>
                <span className="sm:hidden text-xs text-muted-foreground ml-2">lotti</span>
              </div>
              <div className="sm:col-span-3 sm:text-right text-foreground">
                <span className="text-sm font-semibold">{r.quantita_totale}</span>
                <span className="text-xs text-muted-foreground ml-1">{r.unita_misura}</span>
              </div>
              <div className="sm:col-span-2 sm:text-right text-xs text-muted-foreground truncate">
                {r.ultimo_operatore || "—"}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
